import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useContributorsDataQuery$data,
	useContributorsDataQuery$variables,
} from './__generated__/useContributorsDataQuery.graphql.ts';
import type { ParsedMacroParams } from './useParsedMacroParams.ts';

interface UseContributorsDataOptions {
	contentId: string;
	skip?: boolean;
	macroParams?: ParsedMacroParams['macroParamsForQuery'];
}

interface UseContributorsDataResult {
	data: useContributorsDataQuery$data | undefined;
	loading: boolean;
	error: Error | null;
}

export const useContributorsData = ({
	contentId,
	skip,
	macroParams,
}: UseContributorsDataOptions): UseContributorsDataResult => {
	const { data, error, loading } = useQueryInterop<
		useContributorsDataQuery$data,
		useContributorsDataQuery$variables
	>(
		graphql`
			query useContributorsDataQuery(
				$contentId: ID
				$contentStatus: [String]
				$contributorsMacroParameters: ConfluenceContributorsMacroParametersModel
			) {
				singleContent(id: $contentId, status: $contentStatus) {
					id
					contributorsMacro(contributorsMacroParameters: $contributorsMacroParameters) {
						authorRankingList {
							idString
							fullNameString
							edits
							comments
							labels
							watches
							lastEditTime
							lastCommentTime
							lastLabelTime
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
				contentStatus: ['current'],
				contributorsMacroParameters: macroParams ?? undefined,
			},
			skip,
			fetchPolicy: 'cache-first',
		},
	);

	return {
		data: data?.singleContent?.contributorsMacro ? data : undefined,
		loading: Boolean(loading),
		error: error ?? null,
	};
};
